import * as React from 'react';
import { useGetOne } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Divider, Link, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/page-header/PageHeader';
import { BackButton } from '../../components/back-button/BackButton';
import { OrderNote } from '../../components/order-note/OrderNote';
import { PAGE_GUTTER } from '../../core/theme/layout';
import { getBackendLanguage } from '../../core/i18n';
import { CancellationReasonText, OrderStatusChip } from './OrderHistoryList';
import { formatAmount, formatDateTime, lineName, OrderHistoryDetails } from './orderHistoryTypes';

const sectionSx = {
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  p: { xs: 'var(--space-4)', md: 'var(--space-5)' },
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
  minWidth: 0,
};

const sectionTitleSx = {
  fontSize: 'var(--text-xs)',
  fontWeight: 700,
  color: 'var(--color-text-muted)',
  letterSpacing: 'var(--tracking-wide)',
  textTransform: 'uppercase',
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={sectionSx}>
    <Typography sx={sectionTitleSx}>{title}</Typography>
    {children}
  </Box>
);

const Row = ({
  label,
  value,
  strong,
}: {
  label: string;
  value: React.ReactNode;
  strong?: boolean;
}) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="baseline"
    sx={{ gap: 'var(--space-4)' }}
  >
    <Typography
      sx={{ fontSize: 'var(--text-sm)', color: 'text.secondary', fontWeight: strong ? 700 : 400 }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: 'var(--text-sm)', fontWeight: strong ? 700 : 500, textAlign: 'right' }}
    >
      {value}
    </Typography>
  </Stack>
);

export default function OrderHistoryShow(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const {
    data: order,
    isLoading,
    error,
  } = useGetOne<OrderHistoryDetails>(
    'orderHistory',
    // Osobne meta = osobny klucz cache. Lista wklada swoje wiersze do cache getOne pod
    // kluczem bez meta, a wiersz listy nie ma statusHistory ani orderDetails - ekran
    // renderowalby go, zanim przyjda pelne szczegoly z BE.
    { id: Number(id), meta: { details: true } },
    { retry: false },
  );

  const back = <BackButton onClick={() => navigate('/orderHistory')} />;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 'var(--space-16)' }}>
        <CircularProgress />
      </Box>
    );
  }

  // 404 dla zamowienia innego lokalu, nieoplaconego albo anulowanego bez oplaty.
  if (error || !order) {
    return (
      <>
        <PageHeader back={back} title={t('orderHistory.details.title', { number: id })} />
        <Typography sx={{ px: PAGE_GUTTER, color: 'text.secondary' }}>
          {t('orderHistory.details.notFound')}
        </Typography>
      </>
    );
  }

  const details = order.orderDetails;
  const currency = details?.currency ?? 'PLN';
  const money = (value: number | null | undefined) => formatAmount(value, currency, locale);
  const backendLanguage = getBackendLanguage();

  return (
    <>
      <PageHeader
        back={back}
        title={t('orderHistory.details.title', { number: order.orderNumber })}
        subtitle={`${formatDateTime(order.createdAt, locale)} · ${order.locationName}`}
      />
      <Box
        sx={{
          px: PAGE_GUTTER,
          pb: 'var(--space-8)',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(0, 2fr)' },
          gap: 'var(--space-4)',
          alignItems: 'start',
        }}
      >
        <Stack sx={{ gap: 'var(--space-4)', minWidth: 0 }}>
          <Section title={t('orderHistory.details.items')}>
            {(details?.orderListItems ?? []).map((line) => (
              <Stack
                key={line.id}
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ gap: 'var(--space-4)' }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>
                    {line.quantity}× {lineName(line, backendLanguage)}
                  </Typography>
                  {(line.inventoryItemVariants ?? []).map((variant) => {
                    const label = [variant.name, variant.variant].filter(Boolean).join(' ');
                    return (
                      <Typography
                        key={variant.id}
                        sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}
                      >
                        {variant.category ? `${variant.category}: ` : '+ '}
                        {label || '—'}
                      </Typography>
                    );
                  })}
                  {line.discountAmount > 0 && (
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-success)' }}>
                      {line.discountName ?? t('orderHistory.details.promoDiscount')}: −
                      {money(line.discountAmount)}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{ fontSize: 'var(--text-sm)', fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {money(line.price)}
                </Typography>
              </Stack>
            ))}
            <OrderNote note={details?.note} />
            <Divider />
            <Row
              label={t('orderHistory.details.itemSubtotal')}
              value={money(details?.itemSubTotal)}
            />
            {details?.promoDiscountAmount > 0 && (
              <Row
                label={t('orderHistory.details.promoDiscount')}
                value={`−${money(details.promoDiscountAmount)}`}
              />
            )}
            {details?.discountAmount > 0 && (
              <Row
                label={t('orderHistory.details.coupon', { code: details.couponCode ?? '' })}
                value={`−${money(details.discountAmount)}`}
              />
            )}
            {order.orderType === 'DELIVERY' && (
              <Row
                label={t('orderHistory.details.deliveryFee')}
                value={money(details?.deliveryFee)}
              />
            )}
            {order.serviceFee != null && order.serviceFee > 0 && (
              <Row label={t('orderHistory.details.serviceFee')} value={money(order.serviceFee)} />
            )}
            <Divider />
            <Row strong label={t('orderHistory.details.total')} value={money(order.totalPrice)} />
          </Section>
        </Stack>

        <Stack sx={{ gap: 'var(--space-4)', minWidth: 0 }}>
          <Section title={t('orderHistory.details.summary')}>
            <Row
              label={t('orderHistory.columns.status')}
              value={<OrderStatusChip status={order.status} />}
            />
            {order.status === 'CANCELLED' && (
              <Row
                label={t('orderHistory.details.cancellationReason')}
                value={<CancellationReasonText reason={order.cancellationReason} />}
              />
            )}
            <Row
              label={t('orderHistory.columns.orderType')}
              value={t(`orderHistory.orderType.${order.orderType}`)}
            />
            <Row
              label={t('orderHistory.columns.paymentType')}
              value={t(`orderHistory.paymentType.${order.paymentType}`)}
            />
            {order.payment && (
              <Row
                label={t('orderHistory.details.paymentStatus')}
                value={t(`orderHistory.paymentStatus.${order.payment.status}`, {
                  defaultValue: order.payment.status,
                })}
              />
            )}
            {order.payment?.refundedAt && (
              <Row
                label={t('orderHistory.details.refundedAt')}
                value={formatDateTime(order.payment.refundedAt, locale)}
              />
            )}
          </Section>

          <Section title={t('orderHistory.details.customer')}>
            <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>
              {order.customer?.name ?? '—'}
            </Typography>
            {order.customer?.phoneNumber && (
              <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.secondary' }}>
                {order.customer.phoneNumber}
              </Typography>
            )}
            {order.customer?.email && (
              <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.secondary' }}>
                {order.customer.email}
              </Typography>
            )}
            {order.deliveryAddress && (
              <Row
                label={t('orderHistory.details.deliveryAddress')}
                value={order.deliveryAddress}
              />
            )}
            {order.woltTrackingUrl && (
              <Link
                href={order.woltTrackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: 'var(--text-sm)' }}
              >
                {t('orderHistory.details.woltTracking')}
              </Link>
            )}
          </Section>

          <Section title={t('orderHistory.details.timeline')}>
            {(order.statusHistory ?? []).map((entry, index) => (
              <Box key={`${entry.status}-${index}`}>
                <Row
                  label={t(`orderHistory.status.${entry.status}`, { defaultValue: entry.status })}
                  value={formatDateTime(entry.timestamp, locale)}
                />
                {entry.status === 'CANCELLED' && <CancellationReasonText reason={entry.reason} />}
              </Box>
            ))}
          </Section>
        </Stack>
      </Box>
    </>
  );
}
